export const SURVEY_TRACKING_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'referrer'];
const SURVEY_STORAGE_PREFIX = 'TLF_survey_v1';

export function normalizeTrackingValue(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized || null;
}

export function getSurveySlugFromSearch(search) {
  const params = new URLSearchParams(search || '');
  return normalizeTrackingValue(params.get('survey'));
}

export function buildSurveyStorageKey(slug) {
  return `${SURVEY_STORAGE_PREFIX}:${slug}`;
}

export function saveSurveyState(storage, slug, state) {
  if (!storage || !slug) {
    return;
  }
  storage.setItem(buildSurveyStorageKey(slug), JSON.stringify({
    currentPageNo: Number.isInteger(state?.currentPageNo) ? state.currentPageNo : 0,
    data: state?.data && typeof state.data === 'object' ? state.data : {},
  }));
}

export function loadSurveyState(storage, slug) {
  if (!storage || !slug) {
    return {currentPageNo: 0, data: {}};
  }
  const rawValue = storage.getItem(buildSurveyStorageKey(slug));
  if (!rawValue) {
    return {currentPageNo: 0, data: {}};
  }
  try {
    const parsed = JSON.parse(rawValue);
    return {
      currentPageNo: Number.isInteger(parsed?.currentPageNo) ? parsed.currentPageNo : 0,
      data: parsed?.data && typeof parsed.data === 'object' ? parsed.data : {},
    };
  } catch (error) {
    return {currentPageNo: 0, data: {}};
  }
}

export function clearSurveyState(storage, slug) {
  if (!storage || !slug) {
    return;
  }
  storage.removeItem(buildSurveyStorageKey(slug));
}

export function createSurveyMarkdownRenderer(MarkdownItCtor) {
  return new MarkdownItCtor({
    html: true,
    linkify: true,
    breaks: true,
  });
}

function isSurveyLocaleMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const keys = Object.keys(value);
  if (!keys.length) {
    return false;
  }
  return keys.every((key) => key === 'default' || /^[a-z]{2}(?:-[a-z0-9]+)?$/i.test(key)) &&
    keys.every((key) => value[key] === null || value[key] === undefined || typeof value[key] === 'string');
}

export function normalizeSurveyLocale(locale) {
  const normalized = String(locale || '').trim().toLowerCase();
  if (normalized.startsWith('ru')) {
    return 'ru';
  }
  return 'en';
}

export function resolveSurveyLocale(view) {
  const htmlLang = String(view?.document?.documentElement?.lang || '').trim();
  if (htmlLang) {
    return normalizeSurveyLocale(htmlLang);
  }
  const pathLocale = String(view?.location?.pathname || '').split('/').filter(Boolean)[0] || '';
  if (pathLocale) {
    return normalizeSurveyLocale(pathLocale);
  }
  return normalizeSurveyLocale(view?.navigator?.language);
}

export function getLocalizedSurveyText(value, locale = 'en') {
  const normalizedLocale = normalizeSurveyLocale(locale);
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const localizedValue = String(value[normalizedLocale] || '').trim();
    if (localizedValue) {
      return localizedValue;
    }
    const defaultValue = String(value.default || '').trim();
    if (defaultValue) {
      return defaultValue;
    }
    return String(value.en || '').trim();
  }

  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return '';
  }
  if (rawValue.startsWith('{')) {
    try {
      const parsed = JSON.parse(rawValue);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return getLocalizedSurveyText(parsed, normalizedLocale);
      }
    } catch (error) {
    }
  }
  return rawValue;
}

export function applySurveyJsonLocalizationFallbacks(value) {
  if (Array.isArray(value)) {
    return value.map((item) => applySurveyJsonLocalizationFallbacks(item));
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  if (isSurveyLocaleMap(value)) {
    const normalized = {...value};
    if (!String(normalized.default || '').trim() && String(normalized.en || '').trim()) {
      normalized.default = normalized.en;
    }
    return normalized;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => [key, applySurveyJsonLocalizationFallbacks(nestedValue)]),
  );
}

export function renderSurveyPrefix(item, markdownRenderer, locale = 'en') {
  const htmlPrefix = getLocalizedSurveyText(item?.prefix_html, locale);
  if (htmlPrefix) {
    return htmlPrefix;
  }
  const markdownPrefix = getLocalizedSurveyText(item?.prefix_md, locale);
  if (!markdownPrefix || !markdownRenderer) {
    return '';
  }
  return markdownRenderer.render(markdownPrefix);
}

export function hasAnyTrackingData(trackingData) {
  return SURVEY_TRACKING_FIELDS.some((fieldName) => normalizeTrackingValue(trackingData?.[fieldName]) !== null);
}

export function mergeTrackingData(baseData = {}, incomingData = {}) {
  const merged = {};
  SURVEY_TRACKING_FIELDS.forEach((fieldName) => {
    const baseValue = normalizeTrackingValue(baseData[fieldName]);
    const incomingValue = normalizeTrackingValue(incomingData[fieldName]);
    merged[fieldName] = incomingValue ?? baseValue ?? null;
  });
  return merged;
}

export function applyTrackingDataToSurvey(survey, trackingData, rewriteExisting = true) {
  SURVEY_TRACKING_FIELDS.forEach((fieldName) => {
    const question = survey?.getQuestionByName?.(fieldName);
    if (!question) {
      return;
    }
    const incomingValue = normalizeTrackingValue(trackingData?.[fieldName]);
    if (incomingValue === null) {
      return;
    }
    const currentValue = normalizeTrackingValue(survey.getValue(fieldName));
    if (rewriteExisting || currentValue === null) {
      survey.setValue(fieldName, incomingValue);
    }
  });
}

export function collectTrackingPayload({survey, trackingData}) {
  const payload = {};
  SURVEY_TRACKING_FIELDS.forEach((fieldName) => {
    const hasQuestion = Boolean(survey?.getQuestionByName?.(fieldName));
    const fromSurvey = hasQuestion ? normalizeTrackingValue(survey.getValue(fieldName)) : null;
    const fromTracking = normalizeTrackingValue(trackingData?.[fieldName]);
    const finalValue = fromSurvey ?? fromTracking;
    payload[fieldName] = finalValue;
    if (hasQuestion && fromSurvey === null && finalValue !== null) {
      survey.setValue(fieldName, finalValue);
    }
  });
  return payload;
}

export function buildSurveyClientContext(view) {
  return {
    page_url: String(view?.location?.href || ''),
    timezone: String(view?.Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || ''),
    language: String(view?.navigator?.language || ''),
    screen_width: Number.isFinite(view?.innerWidth) ? view.innerWidth : null,
    screen_height: Number.isFinite(view?.innerHeight) ? view.innerHeight : null,
  };
}

export function buildSurveySubmitPayload({surveyData, browserId, trackingData, clientContext}) {
  return {
    data: surveyData && typeof surveyData === 'object' ? surveyData : {},
    browser_id: String(browserId || ''),
    ...mergeTrackingData({}, trackingData),
    client_context: clientContext && typeof clientContext === 'object' ? clientContext : {},
  };
}

export function createTrackingBridge({windowObject, onTrackingData, updateTrackingDataFn}) {
  const pendingResolvers = new Set();

  const handleTrackingMessage = (event) => {
    const payload = event?.data;
    if (!payload || typeof payload !== 'object' || payload.type !== 'utm') {
      return;
    }
    const nextTrackingData = updateTrackingDataFn(payload.data || {});
    onTrackingData?.(nextTrackingData);
    pendingResolvers.forEach((resolver) => resolver(true));
    pendingResolvers.clear();
  };

  windowObject?.addEventListener?.('message', handleTrackingMessage);

  function requestTrackingFromParent(timeoutMs = 700) {
    if (!windowObject || windowObject.parent === windowObject || !windowObject.parent?.postMessage) {
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      let settled = false;
      let timerId = null;

      const resolver = (received) => {
        if (settled) {
          return;
        }
        settled = true;
        if (timerId !== null && windowObject.clearTimeout) {
          windowObject.clearTimeout(timerId);
        }
        pendingResolvers.delete(resolver);
        resolve(received);
      };

      pendingResolvers.add(resolver);
      if (windowObject.setTimeout) {
        timerId = windowObject.setTimeout(() => resolver(false), timeoutMs);
      }

      try {
        windowObject.parent.postMessage({type: 'tlf-tracking-request'}, '*');
      } catch (error) {
        resolver(false);
      }
    });
  }

  function dispose() {
    windowObject?.removeEventListener?.('message', handleTrackingMessage);
    pendingResolvers.clear();
  }

  return {
    handleTrackingMessage,
    requestTrackingFromParent,
    dispose,
  };
}
